import * as React from "react";
import {AppTranslations} from "../../../../components/Utils/translations/AppTranslations";
import {VeBoxModal} from "../../../../components/modal/VeBoxModal";

import "./ErrorDialog.scss";
import {InputAddon, SelectInput, SelectInputOption, TimePickerProps} from "@vebox/etui-core";
import {Content} from "../components/CommandWithValue/data/models/JSONSettingsEquipmentStatus";
import {ColoredCircle} from "../builder/SectionItemBuilder";
import {Display} from "../../../../components/Display";
import {Loader} from "../../../../components/Utils/loader/Loader";
import {SELECT_BORDER_BOX} from "../../../../components/common/etui_components_styles";
import './VisitRegistrationDialog.scss'
import {useEquipmentSettingsStatus} from "../components/CommandWithValue/data/hooks/useEquipmentSettingsStatus";
import {filterStatusSettings} from "../components/CommandWithValue/utils";
import {DateTimePicker} from "../../../../components/common/DateTimePicker";
import {DropdownMenuProps} from "@vebox/etui-core/DropdownMenu";
import {registerTheVisit, updateEquipmentStatus} from "../components/CommandWithValue/data/controller/controllers";
import {dateToLocalISO} from "../../../../dashboard/operativity/shared";

declare var AppTranslations: AppTranslations;

const DATE_TIME_PICKER_STYLES = {
    input: {
        height: "52px",
        width: "220px",
    },
    filterContainer: {width: "100%", p: "0"}
}

const TIME_PICKER_MENU: Partial<DropdownMenuProps> = {
    styles: {
        menuContainer: {width: '220px'},
    }
}

interface VisitRegistrationDialogProps {
    onClose: () => void;
    manageErrorMessage: () => void
    equipmentGuid: string
    reload: () => void
}

export const VisitRegistrationDialog: React.FunctionComponent<VisitRegistrationDialogProps> = ({
                                                                                                   onClose,
                                                                                                   manageErrorMessage,
                                                                                                   equipmentGuid,
                                                                                                   reload
                                                                                               }) => {
    const [content, setContent] = React.useState<Content>(null);
    const [selectedEquipmentStatus, setSelectedEquipmentStatus] = React.useState<SelectInputOption>();
    const [color, setColor] = React.useState<string>();
    const [timestamp, setTimestamp] = React.useState<number>();
    const [notes, setNotes] = React.useState<string>();
    const [isUpdateVisit, setIsUpdateVisit] = React.useState<boolean>();
    const [errorMessage, setErrorMessage] = React.useState<{ type: 'Date' | 'Status' | 'Size', message: string }>();

    const timePickerLabels: TimePickerProps['labels'] = {
        main: AppTranslations.genericWords.time,
        hours: AppTranslations.genericWords.hours,
        minutes: AppTranslations.genericWords.minutes,
    };
    const {isLoading, data, error} = useEquipmentSettingsStatus(equipmentGuid, true)

    const getOption = (value: string) => {
        return options?.find(option => option?.value === value)
    }

    const getOptionColor = (value: string) => {
        return content?.data?.value?.additionalData?.[value]?.color
    }

    const handleStatusChange = (option: SelectInputOption) => {
        setSelectedEquipmentStatus(option)
        setColor(getOptionColor(option?.value))
    }

    const resetErrorMessage = () => {
        if (errorMessage) {
            setErrorMessage(undefined)
        }
    }

    const manageErrorMessages = (type: 'Date' | 'Status' | 'Size', message: string) => {
        let newErrorMessage = message
        if (type === 'Date') {
            if (message.toLowerCase().includes("Old event".toLowerCase())) {
                newErrorMessage = AppTranslations.equipmentSettingsPage.dateNotBeforeAnother
            }
        }
        setErrorMessage({type, message: newErrorMessage})
    }

    const handleNotesChange: React.ChangeEventHandler<HTMLTextAreaElement> = (value) => {
        setNotes(value?.target?.value)
        if (value?.target?.value.length > 255) {
            manageErrorMessages("Size", AppTranslations.errorMessages.tooManyCharacters)
        } else {
            resetErrorMessage()
        }
    }

    function updateDate(e: React.ChangeEvent<{ value: number }>) {
        if (isNaN(e.target.value as any)) {
            return;
        }
        setTimestamp(e.target.value);
        if (new Date().getTime() < e.target.value) {
            manageErrorMessages("Date", AppTranslations.equipmentSettingsPage.dateNotInTheFuture)
        } else {
            resetErrorMessage()
        }
    }

    const submit = async () => {
        setIsUpdateVisit(true)
        try {
            if (content?.data?.value?.value !== selectedEquipmentStatus?.value) {
                await updateEquipmentStatus(equipmentGuid, selectedEquipmentStatus?.value)
            }
        } catch (e) {
            setIsUpdateVisit(false)
            console.error(e)
            manageErrorMessages("Status", e?.message)
            return
        }
        try {
            await registerTheVisit(equipmentGuid, dateToLocalISO(new Date(timestamp)), notes);
            onClose()
            reload()
        } catch (e) {
            console.error(e)
            manageErrorMessages("Date", e?.message);
        } finally {
            setIsUpdateVisit(false);
            setTimeout(() => {
                resetErrorMessage()
            }, 3000)
        }
    }

    const options: SelectInputOption[] = React.useMemo(() => {
        if (!isLoading && content) {
            return Object.keys(content.data.value.options).map(key => ({
                value: key,
                label: content.data.value.options[key]
            }))
        }
        return []
    }, [content, isLoading])

    React.useEffect(() => {
        if (options) {
            setSelectedEquipmentStatus(getOption(content?.data?.value?.value))
            setColor(getOptionColor(content?.data?.value?.value))
        }
    }, [options])

    React.useEffect(() => {
        if (data && !isLoading && !error) {
            setContent(filterStatusSettings(data))
        }
        if (error) {
            console.error(error)
            manageErrorMessage()
        }
    }, [error, isLoading, data])

    return (
        <VeBoxModal className={"main-container"}>
            <VeBoxModal.Header
                title={AppTranslations.massiveActions.censusCooler.visitRegistrationTitle}
                onClose={onClose}
            />
            <VeBoxModal.Section
                style={{
                    height: 'fit-content',
                    width: 'fit-content',
                    minWidth: '500px',
                    maxWidth: '500px',
                    minHeight: '250px',
                }}
            >
                <Display isVisible={isLoading}>
                    <div className={"loader-container-section"}>
                        <Loader relativelyCentered={true}/>
                    </div>
                </Display>
                <Display isVisible={!isLoading}>
                    <DateTimePicker
                        onChange={updateDate}
                        datePickerStyles={DATE_TIME_PICKER_STYLES as any}
                        timePickerStyles={DATE_TIME_PICKER_STYLES as any}
                        timePickerMenuProps={TIME_PICKER_MENU}
                        containerClassName={"flx-space-between flx-row margin-bottom-10"}
                        timePickerLabels={timePickerLabels}
                        datePickerLabel={AppTranslations.genericWords.date}
                    />
                    <Display isVisible={Boolean(errorMessage) && errorMessage?.type === "Date"}>
                        <p className={"data-error-message"}>{errorMessage?.message}</p>
                    </Display>
                    <textarea
                        className={"text-area"}
                        rows={5}
                        placeholder={`${AppTranslations.massiveActions.censusCooler.addNotes} (${AppTranslations.genericWords.optional})`}
                        onChange={handleNotesChange}
                    />
                    <Display isVisible={Boolean(errorMessage) && errorMessage?.type === "Size"}>
                        <p className={"data-error-message"}>{errorMessage?.message}</p>
                    </Display>
                    <p className={"margin-bottom-10"}>{`${AppTranslations.massiveActions.changeEquipmentStatus.changeEquipmentStatusTitle} (${AppTranslations.genericWords.optional})`}</p>
                    <SelectInput
                        onChange={handleStatusChange}
                        name="Equipment Status"
                        label={content?.name}
                        styles={SELECT_BORDER_BOX}
                        options={options}
                        searchable={true}
                        value={selectedEquipmentStatus}
                        defaultValue={getOption(content?.data?.value?.value)}
                        error={Boolean(
                            errorMessage) && errorMessage?.type === "Status" ? {errorMessage: errorMessage?.message} : undefined}
                        startInputAddon={
                            color ?
                                <InputAddon position={"start"}>
                                    <ColoredCircle color={color}/>
                                </InputAddon>
                                : undefined
                        }
                    />
                </Display>
            </VeBoxModal.Section>
            <VeBoxModal.StandardActions
                onSubmit={submit}
                useSubmitLoadingButton={true}
                isSubmitLoading={isUpdateVisit}
                onCancel={onClose}
                canSubmit={!isLoading && !errorMessage}
            />
        </VeBoxModal>
    );
};
